import { ModuleId, type ModuleIds } from "@xmiles/holopix-types";
import {
  SEEDANCE_RATIO_OPTIONS,
  type RatioOption,
} from "@/views/workspace/board/helper/generationConfig";
import type { VideoModelOption } from "../HeroActions/VideoModelSelector.vue";
import type { VideoModeOption, VideoModeValue } from "../HeroActions/VideoModeSelector.vue";
import viduLogoUrl from "../HeroActions/assets/vidu-logo.png";
import seedanceLogoUrl from "../HeroActions/assets/seedance-logo.png";
import minimaxH3LogoUrl from "../HeroActions/assets/minimax-h3-logo.png";

export type VideoModelKey =
  | "vidu-2-pro"
  | "seedance-2-0"
  | "seedance-mini"
  | "minimax-h3"
  | "minimax-h3-fast"
  | "minimax-h3-max";

/** MiniMax H3 / 经济版 / 极速版 共用 Runninghub 交互约束（分辨率字符串、素材校验、尾帧可选等） */
export const isMinimaxH3FamilyModelKey = (modelKey?: string | null): boolean =>
  modelKey === "minimax-h3" || modelKey === "minimax-h3-fast" || modelKey === "minimax-h3-max";

/**
 * 视频质量枚举类型（独立于图片模块的 ImageQuality）。
 * 480p/720p/1080p 为 Vidu/Seedance 档位；768p/2k 为 MiniMax H3 / 经济版；
 * 480p/768p 亦为极速版专属档位（提交时走字符串 480P/768P）。
 */
export type VideoQuality = "480p" | "720p" | "1080p" | "2k" | "768p";

/** 视频质量选项接口（与图片模块的 QualityOption 平行但独立） */
export interface VideoQualityOption {
  value: VideoQuality;
  label: string;
}

/** 视频质量选项数组（value 与 label 一致，替代原 SEEDANCE_QUALITY_OPTIONS） */
export const VIDEO_QUALITY_OPTIONS: VideoQualityOption[] = [
  { value: "480p", label: "480p" },
  { value: "720p", label: "720p" },
  { value: "1080p", label: "1080p" },
];

/**
 * 统一映射函数：VideoQuality → 后端 videoResolution 数值
 *
 * 注意：本函数仅接受 VideoQuality 类型输入，不做旧格式兼容。
 * 旧格式兼容降级逻辑集中在 normalizeCanvasVideoResolution（task.ts）中。
 */
export function videoQualityToResolution(quality: VideoQuality): number {
  const map: Record<VideoQuality, number> = {
    "480p": 480,
    "720p": 720,
    "1080p": 1080,
    // MiniMax H3 档位仅用于展示/兜底，提交时走字符串直传（resolveVideoResolutionParam）
    "2k": 2048,
    "768p": 768,
  };
  return map[quality] ?? 720;
}

export interface VideoModeCapability {
  mode: VideoModeValue;
  maxImages: number;
  ratioOptions: RatioOption[];
  qualityOptions: VideoQualityOption[];
  durationOptions: number[];
  defaultRatio: string;
  defaultQuality: VideoQuality;
  defaultDuration: number;
  /** 单张图片大小上限（MB），未配置则沿用调用方默认值 */
  maxImageSizeMb?: number;
  /** 视频数量上限，未配置表示该模式不支持视频参考 */
  maxVideos?: number;
  /** 单个视频大小上限（MB） */
  maxVideoSizeMb?: number;
  /** 单个视频时长范围 [min, max]（秒） */
  videoDurationRange?: [number, number];
  /** 所有视频总时长上限（秒） */
  maxTotalVideoDuration?: number;
  /** 允许的视频 MIME 类型，未配置则默认 ["video/mp4", "video/quicktime"] */
  allowedVideoMimeTypes?: string[];
  /** 允许的视频扩展名，未配置则默认 ["mp4", "mov"] */
  allowedVideoExtensions?: string[];
  /** 视频宽高比范围 [min, max]，未配置则默认 [0.25, 4] */
  videoAspectRatioRange?: [number, number];
  /** 视频单边像素下限，未配置则默认 128 */
  videoMinPx?: number;
  /** 视频单边像素上限，未配置则不校验上限 */
  videoMaxPx?: number;
  /** 视频平均帧率范围 [min, max]，未配置则不校验 */
  videoFrameRateRange?: [number, number];
}

const VIDU_RATIO_OPTIONS = SEEDANCE_RATIO_OPTIONS.filter((option) => {
  return ["1:1", "16:9", "9:16"].includes(option.value);
});

/** Vidu 质量选项：从 VIDEO_QUALITY_OPTIONS 中过滤掉 480p（Vidu 不支持 480p） */
const VIDU_QUALITY_OPTIONS = VIDEO_QUALITY_OPTIONS.filter((option) => {
  return option.value !== "480p";
});

const createDurationRange = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const VIDEO_MODEL_FALLBACK_KEY: VideoModelKey = "seedance-2-0";
const VIDEO_MODE_FALLBACK: VideoModeValue = "image-to-video";
// v4 规则：总误差 ≤0.2s（5s→5.2s），避免浏览器 duration 浮点偏差误判边界视频
const VIDU_SECOND_VIDEO_DURATION_TOLERANCE_SEC = 0.2;

/**
 * 判断 Vidu 多模态模式下是否允许添加第二个视频。
 * 浏览器读取的 5s 视频时长可能存在浮点偏差（如 5.016s），
 * 使用容差避免将合法边界视频误判为超过 5s。
 */
export const canAddViduSecondVideoByDuration = (duration?: number | null): boolean => {
  if (!duration) return true;
  return duration <= 5 + VIDU_SECOND_VIDEO_DURATION_TOLERANCE_SEC;
};

/** 临时下架的模型列表，后续恢复时从数组中移除对应 key 即可 */
// 2.0 与 mini 均已上线，下架完全由后端 status:0 控制（research.md R6）
export const TEMPORARY_BLOCKED_VIDEO_MODELS: Set<VideoModelKey> = new Set();

/**
 * VideoPanel 入口允许的视频模型白名单（MiniMax H3 仅保留在底部高级输入框入口）。
 * 通过共享选择函数的可选 allowKeys 参数生效，底部入口不传参保持全量。
 */
export const VIDEO_PANEL_ALLOWED_MODEL_KEYS: ReadonlyArray<VideoModelKey> = [
  "vidu-2-pro",
  "seedance-2-0",
  "seedance-mini",
];

/**
 * 返回视频模型选项列表：白名单外模型直接过滤（入口范围限制，从列表中移除），
 * 白名单内但被禁用的模型标记 disabled: true（保持灰显，不隐藏）。
 * @param blockedKeys 禁用模型 key 集合
 * @param allowKeys 可选白名单：传参时仅返回白名单内模型，未传则全量
 */
export const getVideoModelOptionsWithDisabledFlag = (
  blockedKeys: Set<VideoModelKey>,
  allowKeys?: ReadonlyArray<VideoModelKey>,
): VideoModelOption[] => {
  return VIDEO_MODEL_OPTIONS.filter((item) => {
    const key = item.value as VideoModelKey;
    return allowKeys ? allowKeys.includes(key) : true;
  }).map((item) => ({
    ...item,
    disabled: blockedKeys.has(item.value as VideoModelKey),
  }));
};

/**
 * 基于指定禁用集合过滤视频模型选项（仅返回可用模型，过滤掉禁用的）
 * @param blockedKeys 禁用模型 key 集合
 * @param allowKeys 可选白名单：传参时仅返回白名单内模型，未传则全量
 */
export const getVideoModelOptionsWithBlocked = (
  blockedKeys: Set<VideoModelKey>,
  allowKeys?: ReadonlyArray<VideoModelKey>,
): VideoModelOption[] => {
  return VIDEO_MODEL_OPTIONS.filter((item) => {
    const key = item.value as VideoModelKey;
    return (allowKeys ? allowKeys.includes(key) : true) && !blockedKeys.has(key);
  });
};

/**
 * 获取当前可见的视频模型选项（基于 TEMPORARY_BLOCKED_VIDEO_MODELS）
 * @param allowKeys 可选白名单：传参时仅返回白名单内模型，未传则全量
 */
export const getVideoModelOptions = (allowKeys?: ReadonlyArray<VideoModelKey>): VideoModelOption[] => {
  return getVideoModelOptionsWithBlocked(TEMPORARY_BLOCKED_VIDEO_MODELS, allowKeys);
};

export const getDefaultVideoModelKey = (allowKeys?: ReadonlyArray<VideoModelKey>): VideoModelKey => {
  const visibleModelKey = getVideoModelOptions(allowKeys)[0]?.value as VideoModelKey | undefined;
  if (visibleModelKey) return visibleModelKey;
  return (VIDEO_MODEL_OPTIONS[0]?.value as VideoModelKey | undefined) ?? VIDEO_MODEL_FALLBACK_KEY;
};

// ---- MiniMax H3 专属配置（分辨率档位 768p/2k、比例 21:9/16:9/4:3/1:1/3:4/9:16、时长 5-15s） ----

/** H3 质量选项（对应后端 videoResolution 字符串 768p/2k） */
const MINIMAX_H3_QUALITY_OPTIONS: VideoQualityOption[] = [
  { value: "768p", label: "768P" },
  { value: "2k", label: "2K" },
];

/** H3 比例选项：21:9 / 16:9 / 4:3 / 1:1 / 3:4 / 9:16（图生视频自动匹配也按此列表命中，见 helper/video/minimaxAutoRatio.ts） */
export const MINIMAX_H3_RATIO_OPTIONS: RatioOption[] = [
  { value: "21:9", label: "21:9", iconWidth: 20, iconHeight: 9 },
  { value: "16:9", label: "16:9", iconWidth: 20, iconHeight: 11 },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 15 },
  { value: "1:1", label: "1:1", iconWidth: 20, iconHeight: 20 },
  { value: "3:4", label: "3:4", iconWidth: 15, iconHeight: 20 },
  { value: "9:16", label: "9:16", iconWidth: 11, iconHeight: 20 },
];

/** H3 模式选项：参考生视频 / 图生视频 / 首尾帧（文生视频 86 由无素材场景路由） */
const MINIMAX_H3_VIDEO_MODE_OPTIONS: VideoModeOption[] = [
  { value: "reference", label: "多模态参考", icon: "icon-icon_duomotaicankao" },
  { value: "image-to-video", label: "图生视频", icon: "icon-tupian" },
  { value: "start-end", label: "首尾帧", icon: "icon-ICON_shouweizhen" },
];

/** 极速版模式选项：无参考（产品确认）；文生 117 由无素材路由 */
const MINIMAX_H3_MAX_VIDEO_MODE_OPTIONS: VideoModeOption[] = [
  { value: "image-to-video", label: "图生视频", icon: "icon-tupian" },
  { value: "start-end", label: "首尾帧", icon: "icon-ICON_shouweizhen" },
];

/** 极速版质量选项（对应后端 videoResolution 字符串 480P/768P） */
const MINIMAX_H3_MAX_QUALITY_OPTIONS: VideoQualityOption[] = [
  { value: "480p", label: "480P" },
  { value: "768p", label: "768P" },
];

/** 极速版模式能力：仅图生 / 首尾帧，默认 768p，时长 5-15s */
const MINIMAX_H3_MAX_VIDEO_MODE_CAPABILITIES: VideoModeCapability[] = [
  {
    mode: "image-to-video",
    maxImages: 1,
    maxImageSizeMb: 30,
    ratioOptions: [],
    qualityOptions: MINIMAX_H3_MAX_QUALITY_OPTIONS,
    durationOptions: createDurationRange(5, 15),
    defaultRatio: "adaptive",
    defaultQuality: "768p",
    defaultDuration: 5,
  },
  {
    mode: "start-end",
    maxImages: 2,
    maxImageSizeMb: 30,
    ratioOptions: [],
    qualityOptions: MINIMAX_H3_MAX_QUALITY_OPTIONS,
    durationOptions: createDurationRange(5, 15),
    defaultRatio: "adaptive",
    defaultQuality: "768p",
    defaultDuration: 5,
  },
];

/** H3 模式能力：与共享配置 canvas/config.ts 的 minimax-h3 能力表一致（时长 5-15s，默认 768p）
 *  比例规则：image-to-video（图生）/ start-end（首尾帧）无比例选项（Runninghub 宽高比由输入图决定），
 *  reference（多模态参考）保留比例选择。
 */
const MINIMAX_H3_VIDEO_MODE_CAPABILITIES: VideoModeCapability[] = [
  {
    mode: "image-to-video",
    maxImages: 1,
    maxImageSizeMb: 30,
    ratioOptions: [],
    qualityOptions: MINIMAX_H3_QUALITY_OPTIONS,
    durationOptions: createDurationRange(5, 15),
    defaultRatio: "adaptive",
    defaultQuality: "768p",
    defaultDuration: 5,
  },
  {
    mode: "start-end",
    maxImages: 2,
    maxImageSizeMb: 30,
    ratioOptions: [],
    qualityOptions: MINIMAX_H3_QUALITY_OPTIONS,
    durationOptions: createDurationRange(5, 15),
    defaultRatio: "adaptive",
    defaultQuality: "768p",
    defaultDuration: 5,
  },
  {
    mode: "reference",
    // 规则 3/4：多模态图片最多 9 张（JPG/PNG/WEBP）、视频最多 3 段（MP4/MOV、2-15s、≤50MB）
    // 官方 Ref2VA：单段 2-15s 且总时长 ≤15s，缺 maxTotalVideoDuration 会导致多段总时长绕过校验
    maxImages: 9,
    maxImageSizeMb: 30,
    maxVideos: 3,
    maxVideoSizeMb: 50,
    videoDurationRange: [2, 15],
    maxTotalVideoDuration: 15,
    allowedVideoMimeTypes: ["video/mp4", "video/quicktime"],
    allowedVideoExtensions: ["mp4", "mov"],
    videoAspectRatioRange: [0.4, 2.5],
    videoMinPx: 256,
    videoMaxPx: 5760,
    ratioOptions: MINIMAX_H3_RATIO_OPTIONS,
    qualityOptions: MINIMAX_H3_QUALITY_OPTIONS,
    durationOptions: createDurationRange(5, 15),
    defaultRatio: "16:9",
    defaultQuality: "768p",
    defaultDuration: 5,
  },
];

export const VIDEO_MODEL_OPTIONS: VideoModelOption[] = [
  {
    value: "seedance-2-0",
    label: "Seedance 2.0",
    highlights: ["最强视频模型，全能无短板，音画同步生产"],
    icon: seedanceLogoUrl,
    moduleId: ModuleId.SeedanceImage2Video,
  },
  {
    value: "seedance-mini",
    label: "Seedance Mini",
    highlights: ["快一倍，省一半，适合批量试方向"],
    icon: seedanceLogoUrl,
    moduleId: ModuleId.SeedanceMiniImage2Video,
  },
  
  {
    value: "minimax-h3",
    label: "MiniMax H3",
    highlights: ["游戏专项优化，官方原版线路，2K直出"],
    icon: minimaxH3LogoUrl,
    moduleId: ModuleId.MinimaxH3Image2Video,
  },
  {
    value: "minimax-h3-fast",
    label: "MiniMax H3 经济版",
    highlights: ["H3开源部署版本，同等质量，全网最低价"],
    icon: minimaxH3LogoUrl,
    moduleId: ModuleId.MinimaxH3FastImage2Video,
    isRecommended: true,
  },
  {
    value: "minimax-h3-max",
    label: "MiniMax H3 极速版",
    highlights: ["H3最新加速版本，极速生成"],
    icon: minimaxH3LogoUrl,
    moduleId: ModuleId.MinimaxH3MaxImage2Video,
  },
  {
    value: "vidu-2-pro",
    label: "Vidu 2.0 Pro",
    highlights: ["动漫二次元最稳"],
    icon: viduLogoUrl,
    moduleId: ModuleId.CanvasImage2Video,
  },
];

/** 高级输入框视频 Tab 默认模型。不可见时由 resolveVisibleVideoSelection 回退到注册表首项。 */
export const ADVANCED_HERO_DEFAULT_VIDEO_MODEL_KEY: VideoModelKey = "minimax-h3-fast";

/**
 * 判断传入的模型字符串是否为已知视频模型 key（基于 VIDEO_MODEL_OPTIONS 注册表派生）。
 * 模板 model 字段使用与 VideoModelKey 相同的规范值（如 "seedance-mini"）。
 * 新增模型只需注册 VIDEO_MODEL_OPTIONS，无需改动此处。
 */
export const isVideoModelKey = (model?: string | null): model is VideoModelKey => {
  return VIDEO_MODEL_OPTIONS.some((item) => item.value === model);
};

export const VIDU_VIDEO_MODE_OPTIONS: VideoModeOption[] = [
  { value: "image-to-video", label: "图生视频", icon: "icon-tupian" },
  { value: "start-end", label: "首尾帧", icon: "icon-ICON_shouweizhen" },
  { value: "multimodal", label: "多模态参考", icon: "icon-icon_duomotaicankao" },
];

export const SEEDANCE_VIDEO_MODE_OPTIONS: VideoModeOption[] = [
  { value: "reference", label: "全能参考", icon: "icon-icon_duomotaicankao" },
  { value: "image-to-video", label: "图生视频", icon: "icon-tupian" },
  { value: "start-end", label: "首尾帧", icon: "icon-ICON_shouweizhen" },
];

const VIDU_VIDEO_MODE_CAPABILITIES: VideoModeCapability[] = [
  {
    mode: "image-to-video",
    maxImages: 1,
    ratioOptions: VIDU_RATIO_OPTIONS,
    qualityOptions: VIDU_QUALITY_OPTIONS,
    durationOptions: createDurationRange(1, 8),
    defaultRatio: "16:9",
    defaultQuality: "720p",
    defaultDuration: 5,
  },
  {
    mode: "start-end",
    maxImages: 2,
    ratioOptions: VIDU_RATIO_OPTIONS,
    qualityOptions: VIDU_QUALITY_OPTIONS,
    durationOptions: createDurationRange(1, 8),
    defaultRatio: "16:9",
    defaultQuality: "720p",
    defaultDuration: 5,
  },
  {
    mode: "multimodal",
    maxImages: 7,
    maxVideos: 2,
    // Vidu 视频校验：宽高比 [0.25, 4]，单边最小 128px，不校验上限
    allowedVideoMimeTypes: ["video/mp4", "video/quicktime"],
    allowedVideoExtensions: ["mp4", "mov"],
    videoAspectRatioRange: [0.25, 4],
    videoMinPx: 128,
    ratioOptions: VIDU_RATIO_OPTIONS,
    qualityOptions: VIDU_QUALITY_OPTIONS,
    durationOptions: createDurationRange(1, 10),
    defaultRatio: "16:9",
    defaultQuality: "720p",
    defaultDuration: 5,
  },
];

// Seedance 2.0 默认 480p：三个模式默认分辨率统一为 480p，用户仍可手动切换 720p/1080p
const SEEDANCE_VIDEO_MODE_CAPABILITIES: VideoModeCapability[] = [
  {
    mode: "image-to-video",
    maxImages: 9,
    maxImageSizeMb: 30,
    ratioOptions: SEEDANCE_RATIO_OPTIONS,
    qualityOptions: VIDEO_QUALITY_OPTIONS,
    durationOptions: createDurationRange(4, 15),
    defaultRatio: "16:9",
    defaultQuality: "480p",
    defaultDuration: 5,
  },
  {
    mode: "start-end",
    maxImages: 2,
    maxImageSizeMb: 30,
    ratioOptions: SEEDANCE_RATIO_OPTIONS,
    qualityOptions: VIDEO_QUALITY_OPTIONS,
    durationOptions: createDurationRange(4, 15),
    defaultRatio: "16:9",
    defaultQuality: "480p",
    defaultDuration: 5,
  },
  {
    mode: "reference",
    maxImages: 9,
    maxImageSizeMb: 30,
    maxVideos: 3,
    maxVideoSizeMb: 50,
    videoDurationRange: [2, 15],
    maxTotalVideoDuration: 15,
    // Seedance 官方要求：视频宽高比 [0.4, 2.5]，单边 [300, 6000]px
    allowedVideoMimeTypes: ["video/mp4", "video/quicktime"],
    allowedVideoExtensions: ["mp4", "mov"],
    videoAspectRatioRange: [0.4, 2.5],
    videoMinPx: 300,
    videoMaxPx: 6000,
    ratioOptions: SEEDANCE_RATIO_OPTIONS,
    qualityOptions: VIDEO_QUALITY_OPTIONS,
    durationOptions: createDurationRange(4, 15),
    defaultRatio: "16:9",
    defaultQuality: "480p",
    defaultDuration: 5,
  },
];

/** Mini 画质选项：仅 480p/720p，不支持 1080p（FR-004 画质除外） */
const SEEDANCE_MINI_QUALITY_OPTIONS = VIDEO_QUALITY_OPTIONS.filter((option) => option.value !== "1080p");

/**
 * Mini 能力配置：从 SEEDANCE_VIDEO_MODE_CAPABILITIES 派生，仅画质档位不同（480p/720p）。
 * 其余参数与 2.0 同源，保证 2.0 调整时自动同步（research.md R9）。
 */
const SEEDANCE_MINI_VIDEO_MODE_CAPABILITIES: VideoModeCapability[] = SEEDANCE_VIDEO_MODE_CAPABILITIES.map((cap) => ({
  ...cap,
  qualityOptions: SEEDANCE_MINI_QUALITY_OPTIONS,
}));

/**
 * 能力注册表：新增模型只需加一条注册（宪法 II）
 */
const MODEL_CAPABILITIES: Record<VideoModelKey, VideoModeCapability[]> = {
  "vidu-2-pro": VIDU_VIDEO_MODE_CAPABILITIES,
  "seedance-2-0": SEEDANCE_VIDEO_MODE_CAPABILITIES,
  "seedance-mini": SEEDANCE_MINI_VIDEO_MODE_CAPABILITIES,
  "minimax-h3": MINIMAX_H3_VIDEO_MODE_CAPABILITIES,
  "minimax-h3-fast": MINIMAX_H3_VIDEO_MODE_CAPABILITIES,
  "minimax-h3-max": MINIMAX_H3_MAX_VIDEO_MODE_CAPABILITIES,
};

/**
 * 模式选项注册表：新增模型只需加一条注册
 */
const MODEL_MODE_OPTIONS: Record<VideoModelKey, VideoModeOption[]> = {
  "vidu-2-pro": VIDU_VIDEO_MODE_OPTIONS,
  "seedance-2-0": SEEDANCE_VIDEO_MODE_OPTIONS,
  "seedance-mini": SEEDANCE_VIDEO_MODE_OPTIONS,
  "minimax-h3": MINIMAX_H3_VIDEO_MODE_OPTIONS,
  "minimax-h3-fast": MINIMAX_H3_VIDEO_MODE_OPTIONS,
  "minimax-h3-max": MINIMAX_H3_MAX_VIDEO_MODE_OPTIONS,
};

export const getVideoModeOptions = (modelKey: VideoModelKey): VideoModeOption[] => {
  return MODEL_MODE_OPTIONS[modelKey] ?? VIDU_VIDEO_MODE_OPTIONS;
};

export const getDefaultVideoMode = (modelKey: VideoModelKey = getDefaultVideoModelKey()): VideoModeValue => {
  return getVideoModeOptions(modelKey)[0]?.value ?? VIDEO_MODE_FALLBACK;
};

export const resolveVisibleVideoSelection = (
  preferredModelKey?: VideoModelKey | null,
  preferredMode?: VideoModeValue | null,
  allowKeys?: ReadonlyArray<VideoModelKey>,
): { modelKey: VideoModelKey; mode: VideoModeValue } => {
  const visibleModelOptions = getVideoModelOptions(allowKeys);
  const modelKey = visibleModelOptions.some((item) => item.value === preferredModelKey)
    ? (preferredModelKey as VideoModelKey)
    : getDefaultVideoModelKey(allowKeys);
  const modeOptions = getVideoModeOptions(modelKey);
  const mode = modeOptions.some((item) => item.value === preferredMode)
    ? (preferredMode as VideoModeValue)
    : getDefaultVideoMode(modelKey);
  return { modelKey, mode };
};

export const getVideoModeCapability = (modelKey: VideoModelKey, mode: VideoModeValue): VideoModeCapability => {
  const capabilities = MODEL_CAPABILITIES[modelKey] ?? VIDU_VIDEO_MODE_CAPABILITIES;
  return capabilities.find((item) => item.mode === mode) || capabilities[0];
};

export const getVideoMaxImageCount = (modelKey: VideoModelKey, mode: VideoModeValue): number => {
  return getVideoModeCapability(modelKey, mode).maxImages;
};

/** 单张图片大小上限（MB），未配置返回默认值 50 */
export const getVideoMaxImageSizeMb = (modelKey: VideoModelKey, mode: VideoModeValue): number => {
  return getVideoModeCapability(modelKey, mode).maxImageSizeMb ?? 50;
};

/** 视频数量上限，不支持视频参考返回 0 */
export const getVideoMaxVideos = (modelKey: VideoModelKey, mode: VideoModeValue): number => {
  return getVideoModeCapability(modelKey, mode).maxVideos ?? 0;
};

/** 当前模型+模式是否支持视频参考 */
export const canUseVideoReference = (modelKey: VideoModelKey, mode: VideoModeValue): boolean => {
  return getVideoMaxVideos(modelKey, mode) > 0;
};

/** 单个视频大小上限（MB），未配置返回默认值 100 */
export const getVideoMaxVideoSizeMb = (modelKey: VideoModelKey, mode: VideoModeValue): number => {
  return getVideoModeCapability(modelKey, mode).maxVideoSizeMb ?? 100;
};

/** 单个视频时长范围 [min, max]（秒），未配置返回 null */
export const getVideoDurationRange = (modelKey: VideoModelKey, mode: VideoModeValue): [number, number] | null => {
  return getVideoModeCapability(modelKey, mode).videoDurationRange ?? null;
};

/** 所有视频总时长上限（秒），未配置返回 null */
export const getVideoMaxTotalVideoDuration = (modelKey: VideoModelKey, mode: VideoModeValue): number | null => {
  return getVideoModeCapability(modelKey, mode).maxTotalVideoDuration ?? null;
};

/**
 * 视频校验规则集合——从 VideoModeCapability 中提取并填充默认值，
 * 供两个入口（高级输入框 / 视频面板）统一读取，避免硬编码。
 */
export interface VideoValidationRules {
  allowedMime: string[];
  allowedExt: string[];
  maxSizeMb: number;
  aspectRatioRange: [number, number];
  minPx: number;
  /** 像素上限，null 表示不校验 */
  maxPx: number | null;
  /** 尺寸校验失败时的自定义提示，未配置则沿用通用提示 */
  dimensionMessage?: string;
  /** 宽高比校验失败时的自定义提示，未配置则沿用通用提示 */
  aspectRatioMessage?: string;
  durationRange: [number, number] | null;
  maxTotalDuration: number | null;
  frameRateRange: [number, number] | null;
}

/**
 * 获取指定模型+模式的视频校验规则。
 * 未配置的字段回退到 Vidu 时代默认值，确保不破坏现有行为。
 */
export const getVideoValidationRules = (modelKey: VideoModelKey, mode: VideoModeValue): VideoValidationRules => {
  const cap = getVideoModeCapability(modelKey, mode);
  return {
    allowedMime: cap.allowedVideoMimeTypes ?? ["video/mp4", "video/quicktime"],
    allowedExt: cap.allowedVideoExtensions ?? ["mp4", "mov"],
    maxSizeMb: cap.maxVideoSizeMb ?? 100,
    aspectRatioRange: cap.videoAspectRatioRange ?? [0.25, 4],
    minPx: cap.videoMinPx ?? 128,
    maxPx: cap.videoMaxPx ?? null,
    dimensionMessage: isMinimaxH3FamilyModelKey(modelKey)
      ? "视频尺寸不符合要求，视频宽高需在 256‑5760px 范围内，请调整视频尺寸后重试。"
      : undefined,
    aspectRatioMessage: isMinimaxH3FamilyModelKey(modelKey)
      ? "视频比例不符合要求，请不要使用过于狭长 / 宽扁的视频，宽高比需在 0.4‑2.5 范围内。"
      : undefined,
    durationRange: cap.videoDurationRange ?? null,
    maxTotalDuration: cap.maxTotalVideoDuration ?? null,
    frameRateRange: cap.videoFrameRateRange ?? null,
  };
};

export const getVideoFrameRateRange = (modelKey: VideoModelKey, mode: VideoModeValue): [number, number] | null => {
  return getVideoModeCapability(modelKey, mode).videoFrameRateRange ?? null;
};

/** 是否对图片+视频设联合总数限制（Vidu true / Seedance false） */
export const hasJointTotalLimit = (modelKey: VideoModelKey): boolean => {
  return modelKey === "vidu-2-pro";
};

export const getVideoRatioOptions = (modelKey: VideoModelKey, mode: VideoModeValue): RatioOption[] => {
  return getVideoModeCapability(modelKey, mode).ratioOptions;
};

export const getVideoQualityOptions = (modelKey: VideoModelKey, mode: VideoModeValue): VideoQualityOption[] => {
  return getVideoModeCapability(modelKey, mode).qualityOptions;
};

export const getVideoDurationOptions = (modelKey: VideoModelKey, mode: VideoModeValue): number[] => {
  return getVideoModeCapability(modelKey, mode).durationOptions;
};

export const getVideoDefaultRatio = (modelKey: VideoModelKey, mode: VideoModeValue): string => {
  return getVideoModeCapability(modelKey, mode).defaultRatio;
};

export const getVideoDefaultQuality = (modelKey: VideoModelKey, mode: VideoModeValue): VideoQuality => {
  return getVideoModeCapability(modelKey, mode).defaultQuality;
};

export const getVideoDefaultDuration = (modelKey: VideoModelKey, mode: VideoModeValue): number => {
  return getVideoModeCapability(modelKey, mode).defaultDuration;
};

export const resolveVideoModeByModuleId = (modelId?: string | number | null): VideoModeValue => {
  const normalizedId = Number(modelId);
  if (normalizedId === Number(ModuleId.CanvasReferenceVideo)) return "multimodal";
  if (normalizedId === Number(ModuleId.SeedanceReferenceVideo)) return "reference";
  if (normalizedId === Number(ModuleId.SeedanceMiniReferenceVideo)) return "reference";
  if (normalizedId === Number(ModuleId.CanvasStartEndVideo)) return "start-end";
  if (normalizedId === Number(ModuleId.SeedanceStartEndVideo)) return "start-end";
  if (normalizedId === Number(ModuleId.SeedanceMiniStartEndVideo)) return "start-end";
  // 文生视频模块(64)重编辑归位到外部 VideoModeValue 的 'reference'（全能参考），
  // 与 Seedance 全能参考(56)保持一致；详见 data-model.md 第 3.5 节双层枚举说明。
  if (normalizedId === Number(ModuleId.SeedanceText2Video)) return "reference";
  if (normalizedId === Number(ModuleId.SeedanceMiniText2Video)) return "reference";
  if (normalizedId === Number(ModuleId.SeedanceMiniImage2Video)) return "image-to-video";
  // AI创作 Seedance 模块
  if (normalizedId === Number(ModuleId.AiSeedanceReferenceVideo)) return "reference";
  if (normalizedId === Number(ModuleId.AiSeedanceStartEndVideo)) return "start-end";
  if (normalizedId === Number(ModuleId.AiSeedanceText2Video)) return "reference";
  if (normalizedId === Number(ModuleId.AiSeedanceImage2Video)) return "image-to-video";
  // AI创作 Seedance Mini 模块（79-82）
  if (normalizedId === Number(ModuleId.AiSeedanceMiniReferenceVideo)) return "reference";
  if (normalizedId === Number(ModuleId.AiSeedanceMiniStartEndVideo)) return "start-end";
  if (normalizedId === Number(ModuleId.AiSeedanceMiniText2Video)) return "reference";
  if (normalizedId === Number(ModuleId.AiSeedanceMiniImage2Video)) return "image-to-video";
  // MiniMax H3 模块（83-86）：文生视频(86)归位到 reference，与 Seedance 文生(64)一致
  if (normalizedId === Number(ModuleId.MinimaxH3ReferenceVideo)) return "reference";
  if (normalizedId === Number(ModuleId.MinimaxH3StartEndVideo)) return "start-end";
  if (normalizedId === Number(ModuleId.MinimaxH3Text2Video)) return "reference";
  if (normalizedId === Number(ModuleId.MinimaxH3Image2Video)) return "image-to-video";
  // MiniMax H3 Fast / 经济版 模块（106-109）：文生视频(109)归位到 reference
  if (normalizedId === Number(ModuleId.MinimaxH3FastReferenceVideo)) return "reference";
  if (normalizedId === Number(ModuleId.MinimaxH3FastStartEndVideo)) return "start-end";
  if (normalizedId === Number(ModuleId.MinimaxH3FastText2Video)) return "reference";
  if (normalizedId === Number(ModuleId.MinimaxH3FastImage2Video)) return "image-to-video";
  // MiniMax H3 Max / 极速版（115/116/117）：无 reference UI；文生(117)归位到 image-to-video
  if (normalizedId === Number(ModuleId.MinimaxH3MaxStartEndVideo)) return "start-end";
  if (normalizedId === Number(ModuleId.MinimaxH3MaxImage2Video)) return "image-to-video";
  if (normalizedId === Number(ModuleId.MinimaxH3MaxText2Video)) return "image-to-video";
  return "image-to-video";
};

export const resolveVideoModelKeyByModuleId = (modelId?: string | number | null): VideoModelKey => {
  const normalizedId = Number(modelId);
  if (
    normalizedId === Number(ModuleId.CanvasImage2Video)
    || normalizedId === Number(ModuleId.CanvasReferenceVideo)
    || normalizedId === Number(ModuleId.CanvasStartEndVideo)
  ) {
    return "vidu-2-pro";
  }
  // mini 专属模块须在 2.0 fall-through 之前命中，避免误归 2.0（FR-010）
  if (
    normalizedId === Number(ModuleId.SeedanceMiniStartEndVideo)
    || normalizedId === Number(ModuleId.SeedanceMiniReferenceVideo)
    || normalizedId === Number(ModuleId.SeedanceMiniImage2Video)
    || normalizedId === Number(ModuleId.SeedanceMiniText2Video)
  ) {
    return "seedance-mini";
  }
  // AI创作 Seedance 模块
  if (
    normalizedId === Number(ModuleId.AiSeedanceStartEndVideo)
    || normalizedId === Number(ModuleId.AiSeedanceReferenceVideo)
    || normalizedId === Number(ModuleId.AiSeedanceImage2Video)
    || normalizedId === Number(ModuleId.AiSeedanceText2Video)
  ) {
    return "seedance-2-0";
  }
  // AI创作 Seedance Mini 模块（79-82）
  if (
    normalizedId === Number(ModuleId.AiSeedanceMiniStartEndVideo)
    || normalizedId === Number(ModuleId.AiSeedanceMiniReferenceVideo)
    || normalizedId === Number(ModuleId.AiSeedanceMiniImage2Video)
    || normalizedId === Number(ModuleId.AiSeedanceMiniText2Video)
  ) {
    return "seedance-mini";
  }
  // MiniMax H3 模块（83-86）
  if (
    normalizedId === Number(ModuleId.MinimaxH3StartEndVideo)
    || normalizedId === Number(ModuleId.MinimaxH3ReferenceVideo)
    || normalizedId === Number(ModuleId.MinimaxH3Image2Video)
    || normalizedId === Number(ModuleId.MinimaxH3Text2Video)
  ) {
    return "minimax-h3";
  }
  // MiniMax H3 Fast / 经济版 模块（106-109）
  if (
    normalizedId === Number(ModuleId.MinimaxH3FastStartEndVideo)
    || normalizedId === Number(ModuleId.MinimaxH3FastReferenceVideo)
    || normalizedId === Number(ModuleId.MinimaxH3FastImage2Video)
    || normalizedId === Number(ModuleId.MinimaxH3FastText2Video)
  ) {
    return "minimax-h3-fast";
  }
  // MiniMax H3 Max / 极速版 模块（115/116/117）
  if (
    normalizedId === Number(ModuleId.MinimaxH3MaxStartEndVideo)
    || normalizedId === Number(ModuleId.MinimaxH3MaxImage2Video)
    || normalizedId === Number(ModuleId.MinimaxH3MaxText2Video)
  ) {
    return "minimax-h3-max";
  }
  return "seedance-2-0";
};

/**
 * 视频生成路由注册表：VideoModelKey × VideoModeValue → ModuleIds
 * 新增模型只需加一条注册，零分支改动（宪法 II）
 */
const VIDEO_GENERATE_MODULE_MAP: Record<VideoModelKey, Partial<Record<VideoModeValue, ModuleIds>>> = {
  "vidu-2-pro": {
    "multimodal": ModuleId.CanvasReferenceVideo,
    "start-end": ModuleId.CanvasStartEndVideo,
    "image-to-video": ModuleId.CanvasImage2Video,
  },
  "seedance-2-0": {
    "reference": ModuleId.SeedanceReferenceVideo,
    "start-end": ModuleId.SeedanceStartEndVideo,
    "image-to-video": ModuleId.SeedanceImage2Video,
  },
  "seedance-mini": {
    "reference": ModuleId.SeedanceMiniReferenceVideo,
    "start-end": ModuleId.SeedanceMiniStartEndVideo,
    "image-to-video": ModuleId.SeedanceMiniImage2Video,
  },
  // MiniMax H3：83/84/85 对应首尾帧/参考生视频/图生视频；文生(86)由无素材路由
  "minimax-h3": {
    "reference": ModuleId.MinimaxH3ReferenceVideo,
    "start-end": ModuleId.MinimaxH3StartEndVideo,
    "image-to-video": ModuleId.MinimaxH3Image2Video,
  },
  "minimax-h3-fast": {
    "reference": ModuleId.MinimaxH3FastReferenceVideo,
    "start-end": ModuleId.MinimaxH3FastStartEndVideo,
    "image-to-video": ModuleId.MinimaxH3FastImage2Video,
  },
  "minimax-h3-max": {
    "start-end": ModuleId.MinimaxH3MaxStartEndVideo,
    "image-to-video": ModuleId.MinimaxH3MaxImage2Video,
  },
};

/**
 * 文生视频模块注册表：无素材时跨模式统一走文生视频通道
 */
const TEXT_TO_VIDEO_MODULE_MAP: Partial<Record<VideoModelKey, ModuleIds>> = {
  "seedance-2-0": ModuleId.SeedanceText2Video,
  "seedance-mini": ModuleId.SeedanceMiniText2Video,
  "minimax-h3": ModuleId.MinimaxH3Text2Video,
  "minimax-h3-fast": ModuleId.MinimaxH3FastText2Video,
  "minimax-h3-max": ModuleId.MinimaxH3MaxText2Video,
};

/**
 * 底部高级输入框展示用代理模块 ID，与生成 moduleId 同源（注册表查表）。
 */
export const getVideoDisplayModuleId = (modelKey: VideoModelKey, mode: VideoModeValue): ModuleIds => {
  return VIDEO_GENERATE_MODULE_MAP[modelKey]?.[mode] ?? ModuleId.SeedanceImage2Video;
};

export const getVideoGenerateModuleId = (modelKey: VideoModelKey, mode: VideoModeValue): ModuleIds => {
  return VIDEO_GENERATE_MODULE_MAP[modelKey]?.[mode] ?? ModuleId.SeedanceImage2Video;
};

/**
 * 按模型 + 模式 + 是否有素材，动态选择视频生成后端模块。
 *
 * 路由规则（spec FR-002）：
 * - Seedance 家族（2.0 / mini）+ 无素材 → 文生视频模块（注册表查 TEXT_TO_VIDEO_MODULE_MAP），
 *   跨所有可见模式统一走该通道。
 * - 其余情况 → 委托 {@link getVideoGenerateModuleId} 走当前模式原通道。
 *
 * @param modelKey  视频模型标识
 * @param mode      用户可见模式（外部 VideoModeValue）
 * @param hasAssets 是否存在任何参考素材（图片或视频，任一存在即为 true）
 * @returns 后端模块 ID
 */
export const getVideoGenerateModuleIdWithAssets = (
  modelKey: VideoModelKey,
  mode: VideoModeValue,
  hasAssets: boolean,
): ModuleIds => {
  if (!hasAssets && TEXT_TO_VIDEO_MODULE_MAP[modelKey]) {
    return TEXT_TO_VIDEO_MODULE_MAP[modelKey]!;
  }
  return getVideoGenerateModuleId(modelKey, mode);
};

/** VideoModelKey → 后端 API 接收的 videoModel 字段值 */
const VIDEO_MODEL_KEY_TO_API_MODEL: Record<VideoModelKey, string> = {
  "vidu-2-pro": "vidu-q2-pro",
  "seedance-2-0": "seedance-2-0",
  "seedance-mini": "seedance-mini",
  // H3 使用独立模型标识 minimax-h3，后端据此路由到 Runninghub hailuo-h3
  "minimax-h3": "minimax-h3",
  "minimax-h3-fast": "minimax-h3-fast",
  "minimax-h3-max": "minimax-h3-max",
};

export const getVideoApiModelName = (modelKey: VideoModelKey): string => {
  return VIDEO_MODEL_KEY_TO_API_MODEL[modelKey] ?? modelKey;
};

/**
 * 后端 API model 字段 → 前端 VideoModelKey 精确匹配表（FR-010，禁止 includes 子串匹配）
 */
const API_MODEL_TO_VIDEO_MODEL_KEY: Record<string, VideoModelKey> = {
  "seedance-2-0": "seedance-2-0",
  "seedance-2.0": "seedance-2-0",
  "seedance-mini": "seedance-mini",
  "seedance_mini": "seedance-mini",
  "vidu-2-pro": "vidu-2-pro",
  "vidu-q2-pro": "vidu-2-pro",
  "vidu_q2_pro": "vidu-2-pro",
  "vidu 2.0 pro": "vidu-2-pro",
  "vidu q2 pro": "vidu-2-pro",
  "minimax-h3": "minimax-h3",
  "minimax-h3-fast": "minimax-h3-fast",
  "minimax-h3-max": "minimax-h3-max",
};

/** MiniMax H3 的 4 个 module：83/84/85/86 */
export const MINIMAX_H3_MODULE_IDS = [
  ModuleId.MinimaxH3StartEndVideo,
  ModuleId.MinimaxH3ReferenceVideo,
  ModuleId.MinimaxH3Image2Video,
  ModuleId.MinimaxH3Text2Video,
] as const;

/** MiniMax H3 Fast / 经济版 的 4 个 module：106/107/108/109 */
export const MINIMAX_H3_FAST_MODULE_IDS = [
  ModuleId.MinimaxH3FastStartEndVideo,
  ModuleId.MinimaxH3FastReferenceVideo,
  ModuleId.MinimaxH3FastImage2Video,
  ModuleId.MinimaxH3FastText2Video,
] as const;

/** MiniMax H3 Max / 极速版 的 3 个 module：115 首尾帧 / 116 图生 / 117 文生 */
export const MINIMAX_H3_MAX_MODULE_IDS = [
  ModuleId.MinimaxH3MaxStartEndVideo,
  ModuleId.MinimaxH3MaxImage2Video,
  ModuleId.MinimaxH3MaxText2Video,
] as const;

/** 判断 moduleId 是否为 MiniMax H3 模块（83-86） */
export const isMinimaxH3ModuleId = (moduleId?: string | number | null): boolean => {
  const id = Number(moduleId);
  return Number.isFinite(id) && (MINIMAX_H3_MODULE_IDS as readonly number[]).includes(id);
};

/** 判断 moduleId 是否为 MiniMax H3 Fast / 经济版 模块（106-109） */
export const isMinimaxH3FastModuleId = (moduleId?: string | number | null): boolean => {
  const id = Number(moduleId);
  return Number.isFinite(id) && (MINIMAX_H3_FAST_MODULE_IDS as readonly number[]).includes(id);
};

/** 判断 moduleId 是否为 MiniMax H3 Max / 极速版 模块（115/116/117） */
export const isMinimaxH3MaxModuleId = (moduleId?: string | number | null): boolean => {
  const id = Number(moduleId);
  return Number.isFinite(id) && (MINIMAX_H3_MAX_MODULE_IDS as readonly number[]).includes(id);
};

/** 判断 moduleId 是否为 MiniMax 家族，分辨率走字符串直传（H3/经济版 768P·2K；极速版 480P·768P） */
export const isMinimaxStringResolutionModuleId = (moduleId?: string | number | null): boolean =>
  isMinimaxH3ModuleId(moduleId) || isMinimaxH3FastModuleId(moduleId) || isMinimaxH3MaxModuleId(moduleId);

/**
 * 提交给后端的 videoResolution 参数。
 *
 * MiniMax 分辨率档位是字符串枚举（与 Vidu/Seedance 数字体系不同），必须直传大写枚举。
 *
 * @param moduleId 生成 module（用于识别 MiniMax 家族）
 * @param quality  前端质量档位（小写）
 */
export const resolveVideoResolutionParam = (
  moduleId: number | undefined,
  quality: VideoQuality,
): number | string => {
  if (isMinimaxStringResolutionModuleId(moduleId)) {
    if (isMinimaxH3MaxModuleId(moduleId)) {
      const maxMap: Record<string, string> = { "480p": "480P", "768p": "768P" };
      return maxMap[quality] ?? "768P";
    }
    const h3Map: Record<string, string> = { "768p": "768P", "2k": "2K" };
    return h3Map[quality] ?? "768P";
  }
  return videoQualityToResolution(quality);
};

/**
 * 将后端 API 返回的 model 字段映射为前端 VideoModelKey
 * 精确匹配，禁止 includes 子串匹配（FR-010），
 * 避免 "seedance-mini" 被误判为 "seedance-2-0"。
 */
export const mapApiModelToVideoModelKey = (videoModel?: string | null): VideoModelKey | undefined => {
  const normalizedVideoModel = String(videoModel || "").trim().toLowerCase();
  if (!normalizedVideoModel) return undefined;
  return API_MODEL_TO_VIDEO_MODEL_KEY[normalizedVideoModel];
};

/**
 * 将回填/重编辑来源中的模型与模式归一化到“当前可见模型 + 该模型支持的模式”。
 * 被临时屏蔽的模型不会继续停留在内部状态，而是自动收敛到可见模型。
 */
export const resolveVisibleVideoFillSelection = (payload: {
  moduleId?: string | number | null;
  modelKey?: VideoModelKey | null;
  mode?: VideoModeValue | null;
  videoModel?: string | null;
}, allowKeys?: ReadonlyArray<VideoModelKey>): { modelKey: VideoModelKey; mode: VideoModeValue } => {
  const normalizedModuleId = Number(payload.moduleId);
  // 业务规则：H3 / H3 Fast 必须以 module 为准回填模型，避免 videoModel 误归到其它模型。
  const isMinimaxFamilyModule = isMinimaxStringResolutionModuleId(normalizedModuleId);
  const preferredModelKey = payload.modelKey
    ?? (isMinimaxFamilyModule
      ? resolveVideoModelKeyByModuleId(normalizedModuleId)
      : mapApiModelToVideoModelKey(payload.videoModel))
    ?? (Number.isFinite(normalizedModuleId) && normalizedModuleId > 0
      ? resolveVideoModelKeyByModuleId(normalizedModuleId)
      : undefined);
  const preferredMode = payload.mode
    ?? (Number.isFinite(normalizedModuleId) && normalizedModuleId > 0
      ? resolveVideoModeByModuleId(normalizedModuleId)
      : undefined);

  return resolveVisibleVideoSelection(preferredModelKey, preferredMode, allowKeys);
};

export const resolveFirstVideoModelKeyByMode = (mode: VideoModeValue): VideoModelKey => {
  const matchedVisibleModel = getVideoModelOptions().find((model) => {
    return getVideoModeOptions(model.value as VideoModelKey).some((item) => item.value === mode);
  });
  if (matchedVisibleModel) return matchedVisibleModel.value as VideoModelKey;
  return getDefaultVideoModelKey();
};

export const normalizeVideoSelection = (
  modelKey: VideoModelKey,
  mode: VideoModeValue,
  payload: {
    ratio: string;
    quality: VideoQuality;
    duration: number;
  },
) => {
  const capability = getVideoModeCapability(modelKey, mode);

  return {
    ratio: capability.ratioOptions.some((item) => item.value === payload.ratio)
      ? payload.ratio
      : capability.defaultRatio,
    quality: capability.qualityOptions.some((item) => item.value === payload.quality)
      ? payload.quality
      : capability.defaultQuality,
    duration: (() => {
      const dur = Number(payload.duration);
      if (capability.durationOptions.includes(dur)) return dur;
      // 不在合法选项中时，clamp 到能力边界（非回退默认值）
      const minDur = Math.min(...capability.durationOptions);
      const maxDur = Math.max(...capability.durationOptions);
      return Math.max(minDur, Math.min(dur, maxDur));
    })(),
  };
};

/**
 * 跨模型的 VideoModeValue 等价映射表（双向）。
 * 用于模型切换时保留用户意图，例如：
 * - Seedance "reference"（全能参考）↔ Vidu "multimodal"（多模态参考）
 * - "image-to-video" 和 "start-end" 两边同名，无需映射
 *
 * 新增等价关系只需加一条映射，零分支改动。
 */
const VIDEO_MODE_EQUIVALENCE: Record<string, VideoModeValue> = {
  reference: "multimodal",
  multimodal: "reference",
};

/**
 * 解析当前模式在另一模型中的等价模式。
 * 无等价映射时原样返回，调用方自行判断目标模型是否支持。
 */
export const resolveEquivalentMode = (mode: VideoModeValue): VideoModeValue => {
  return VIDEO_MODE_EQUIVALENCE[mode] ?? mode;
};
